import LoadingSpinner from 'components/elements/Spinner/LoadingSpinner';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useAuth } from 'services/auth-service';
import AccountService, { AccountProps } from 'services/hooks/account-service';
import TopBar from 'components/elements/TopBar';
import { useQuery } from 'react-query';
import { Box, Button, Card, CardActions, CardContent, Grid, Typography } from '@mui/material';
import { colours } from 'utils/constants';
import { BuildingProps } from 'services/hooks/building-service';
import UpdateForm from './AccountForms/save';
import { toast } from 'react-toastify';
import { generateApiFailErrorMessage } from 'utils/helpers';

export default function AccountViewMode() {
  const {
    state: { user }
  } = useAuth();
  const [updateShowModal, setUpdateShowModal] = useState(false);
  const [editUUID, setEditUUID] = useState('');

  /* The code is using the `useQuery` hook from the `react-query` library to fetch account data. */
  const { data, isLoading, refetch } = useQuery<AccountProps, Error>(
    ['account', user.uuid],
    () => AccountService.findAccountById(user.uuid),
    {
      enabled: user.role === 'ACCOUNT' && !!user.uuid,
      onError: () => {
        toast.error(generateApiFailErrorMessage('Account'));
      }
    }
  );

  useEffect(() => {
    refetch();
  }, [refetch, updateShowModal]);

  // Handle Menu Toggle (for mobile Drawer)
  const handleManage = () => {
    setEditUUID(user?.uuid ? user.uuid : '');
    setUpdateShowModal(true);
  };

  // Render Account Card
  const renderAccountCard = (account: AccountProps) => {
    return (
      <React.Fragment>
        <CardContent>
          <Typography variant="h4" component="div">
            {account?.name}
          </Typography>
          <Box
            display="flex"
            flexDirection={{ xs: 'column', sm: 'row' }} // Change flexDirection to `column` for `xs` screens and to `row` for `sm` screens and above
            sx={{ mt: 2 }}
            justifyContent="space-between"
          >
            {/* Contract Duration */}
            <Box flex="1" paddingRight="20px">
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Contract Duration
              </Typography>
              <Typography variant="body2">
                {account?.contractStartDate &&
                  `${format(new Date(account?.contractStartDate), 'dd MMMM yyyy')} to ${format(
                    new Date(account?.contractEndDate),
                    'dd MMMM yyyy'
                  )}`}
              </Typography>
            </Box>
            {/* List of Buildings */}
            <Box
              flex="1"
              sx={{
                paddingTop: { xs: '10px', sm: '0' } // Add padding on `xs` screens and no padding on `sm` screens and above
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                Building(s)
              </Typography>
              <div>
                {account?.buildings && account?.buildings.length > 0 ? (
                  account?.buildings.map((building: BuildingProps, index: number) => {
                    return (
                      <Typography key={building.uuid || index} variant="body2">
                        {building?.name}
                      </Typography>
                    );
                  })
                ) : (
                  <Typography variant="body2">No Buildings Found for the Account</Typography>
                )}
              </div>
            </Box>
          </Box>
        </CardContent>
        <CardActions>
          <Button
            size="small"
            style={{ textTransform: 'none' }}
            sx={{ color: colours.seafoam, fontWeight: 'bold' }}
            onClick={handleManage}
          >
            Manage
          </Button>
        </CardActions>
      </React.Fragment>
    );
  };

  return !isLoading && data ? (
    <>
      {updateShowModal && (
        <UpdateForm
          entityName={`Update Account`}
          showModal={updateShowModal}
          setShowModal={setUpdateShowModal}
          setEditUUID={setEditUUID}
          editQuery={AccountService.findAccountById}
          editUUID={editUUID}
          createQuery={AccountService.createAccount}
          updateQuery={AccountService.updateAccount}
          queryName={'account'}
        />
      )}
      <div className="space-y-4">
        <TopBar setGlobalFilter={false} isMessage={true} />
        <h1 className="p-0">Account</h1>
        <Grid
          container
          spacing={4}
          justifyContent="flex-start"
          sx={{
            '&::after': {
              content: '" "',
              flex: 'auto'
            }
          }}
        >
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Card variant="outlined" sx={{ borderRadius: '8px', width: '100%' }}>
              {renderAccountCard(data)}
            </Card>
          </Grid>
        </Grid>
      </div>
    </>
  ) : (
    <div>
      <LoadingSpinner />
    </div>
  );
}
